import {Injectable} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {environment} from '../../../../src/environments/environment';
import {ObjectModel, ObjectParamView} from './object.model';
import {BehaviorSubject, EMPTY, forkJoin, Observable} from 'rxjs';
import {catchError, flatMap, shareReplay} from 'rxjs/operators';
import {ParentApiService, SingleRESTService} from '../../../../src/app/API/apiService';
import { ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';

import {Measure} from '../measure/measure.model';
import {UnitModel} from '../unit/unit.model';
import {ExtattrvalModel} from '../extattr/extattrval.model';
import {ObjectShortModel} from './object.short.model';
import {ObjectsFetchOptions} from './objects-api.service';

@Injectable()
export class ObjectApiService extends ParentApiService  implements SingleRESTService<ObjectModel> {


  timeout = 2 * 60 * 1000; // 5 минут

  private baseUrl = environment.backEndUrlPrefix + '/' + 'db/object';
  private baseUnitUrl = environment.backEndUrlPrefix + '/' + 'db/unit';
  private url = environment.backEndUrlPrefix + '/';
  private paramsUrl = environment.backEndUrlPrefix + '/' + 'db/';

  private measureUrl = environment.backEndUrlPrefix + '/measure';


  private _objects = new BehaviorSubject<ObjectModel[]>([]);
  private dataStore: { objects: ObjectModel[] } = {objects: []}; // store our data in memory
  get objects() {
    return this._objects.asObservable();
  }

  constructor(private http: HttpClient) {
    super();
  }

  getNewObject(): ObjectModel {
    return {};
  }

  loadAll() {
    const code = 'loadAll';
    const cache = this.getCache(code);
    if (!cache) {
      this.http.get<ObjectModel[]>(this.baseUrl).subscribe(
        data => {
          this.dataStore.objects = data;
          this._objects.next(Object.assign({}, this.dataStore).objects);
        },
        error => console.log('Could not load todos.')
      );
      this.setCache(code, true);
    }
  }


  getAll(params: {sensorLocations: boolean} = {sensorLocations: false}, options?: ObjectsFetchOptions) {
    // Флаги в ключе кэша — чтобы разные вызовы getAll() с разным набором withTags/withStates/withControllers
    // не подменяли данные друг друга (кэш общий на все вызовы этого сервиса).
    const code = `all:${params.sensorLocations}:${options?.withTags ?? true}:${options?.withStates ?? true}:${options?.withControllers ?? true}`;
    let cache = this.getCache(code);

    if (!cache) {
      const httpParams: Record<string, boolean> = {};
      if (params.sensorLocations) {
        httpParams.returnSensorLocations = true;
      }
      if (options?.withTags !== undefined) {
        httpParams.withTags = options.withTags;
      }
      if (options?.withStates !== undefined) {
        httpParams.withStates = options.withStates;
      }
      if (options?.withControllers !== undefined) {
        httpParams.withControllers = options.withControllers;
      }
      cache = this.http.get<ObjectModel[]>(this.baseUrl, {params: httpParams}).pipe(
        shareReplay(1),
        catchError(err => {
          delete this.cache[code];
          return EMPTY;
        }));
      this.setCache(code, cache);
    } else {
    }
    return cache;
  }


  getAllShortest(): Observable<ObjectShortModel[]> {
    return this.http.get<ObjectShortModel[]>(this.baseUrl + '/shortest');
  }

  deleteGroup(ids: number[]) {
    return this.http.delete<string>(this.baseUrl, {params: {id: ids.map(id => String(id))}});
  }

  byWidgetRubric(id) {
    return this.http.get<ObjectModel[]>(`${this.baseUrl}/child/objrubric/${id}`);
  }

  byId(object_id): Observable<ObjectModel> {
    return this.http.get<ObjectModel>(this.baseUrl + `/${object_id}`);
  }

  getTraceById(object_id): Observable<any> {
    return this.http.get<ObjectModel>(this.baseUrl + `/${object_id}`).pipe(
      flatMap(res => {
        const requestlist = [];
        const observable = new Observable((observer) => {
          observer.next(res);
          observer.complete();
        });
        requestlist.push(observable);
        if (res.geoPositionParam) {
          const geoPositionParam = res.geoPositionParam;
          const from = new Date().getTime() - 24 * 3600 * 1000;
          const to = new Date().getTime();
          const limit = 5000;
          const offset = 0;
          const params: any = {from: `${from}`, to: `${to}`, limit: `${limit}`, offset: `${offset}`};
          params.cdpId = geoPositionParam.id;
          requestlist.push(this.http.get<Measure[]>(`${this.measureUrl}/period`, {params}));
        }
        return forkJoin(requestlist);
      }));
  }


  getCDP(objectId, params = {}) {
    return this.http.get<any>(this.paramsUrl + `contrdeviceparam`, {params: {...params, objectId}});
  }

  getCCP(objectId, params = {}) {
    return this.http.get<any>(this.paramsUrl + `contrcalcparam`, {params: {...params, objectId}});
  }

  getParams(object_id, params: { type?: string; calc?: string; deviceClassifierId?: string[] } = {}) {
    return this.http.get<ObjectParamView[]>(this.baseUrl + `/${object_id}/params`, {params});
  }

  getUnits(object_id, params: { type?: string; calc?: string; deviceClassifierId?: string[] } = {}) {
    return this.http.get<UnitModel[]>(this.baseUrl + `/${object_id}/unit`, {params});
  }

  getUnits2(params: { objectId?: string[]; deviceClassifierId?: string} = {}) {
    return this.http.get<UnitModel[]>(this.baseUnitUrl, {params});
  }

  getDashboards(object_id, params = {}) {
    return this.http.get<any>(this.baseUrl + `/${object_id}/dashboard`, {params});
  }

  getAttributes(object_id, params = {}) {
    //return this.http.get<any>(this.baseUrl + `/${object_id}/attrval`, {params});
    return this.http.get<any>(`${environment.backEndUrlPrefix}/object_attr?objectId=${object_id}`, {params});
  }

  getRubricAttributes(rubric_id, params = {}) {
    //return this.http.get<any>(this.baseUrl + `/${object_id}/attrval`, {params});
    return this.http.get<any>(`${environment.backEndUrlPrefix}/rubric_attr?rubricId=${rubric_id}`, {params});
  }
  createObjectAttr(data: ExtattrvalModel, id: number): Observable<ExtattrvalModel> {
    return this.http.post<ExtattrvalModel>(`${this.url}object_attr`, {...data, object: {id: +id}});
  }
  getControllers(object_id, params = {}) {
    return this.http.get<any>(environment.backEndUrlPrefix + `/db/controller/child/object/${object_id}`, {params});
  }

  create(data: ObjectModel): Observable<ObjectModel> {
    return this.http.post<ObjectModel>(`${this.baseUrl}`, data);
  }

  save(objectId: number, data: ObjectModel): Observable<ObjectModel> {
    return this.http.put<ObjectModel>(`${this.baseUrl}/${objectId}`, data);
  }

  deleteOperatingMode(objectId: number): Observable<any> {
    return this.http.post<ObjectModel>(`${this.baseUrl}/operatingmode/update?id=${objectId}`, []);
  }

  resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<ObjectModel> | Promise<ObjectModel> | ObjectModel {
    return this.byId(route.params.objectId);
  }
}

